import { useCallback } from 'react';
import { getUploadPresignedUrl, getFetchPresignedUrl } from '../services/presigned-url';

export const useS3Uploader = () => {
  const uploadFileToS3 = useCallback(
    async (file: File, fileName: string, pathPrefix: string): Promise<string | null> => {
      if (!file) {
        console.error('No file provided for upload.');
        return null;
      }
      try {
        const fullFileKey = `${pathPrefix}/${fileName}`;
        // 1. Get presigned upload URL from backend
        const presignedResponse = await getUploadPresignedUrl({
          fileKey: fullFileKey,
          fileType: file.type || 'application/octet-stream'
        });
        // New API response: { generatePresignedUrl: { uploadUrl, fileKey } }
        const uploadUrl = presignedResponse?.generatePresignedUrl?.uploadUrl;
        const uploadedFileKey = presignedResponse?.generatePresignedUrl?.fileKey;
        if (!uploadUrl || !uploadedFileKey) {
          throw new Error('No presigned upload URL or fileKey received from backend');
        }
        // 2. Upload file to S3 using the presigned URL
        const arrayBuffer = await file.arrayBuffer();
        await fetch(uploadUrl, {
          method: 'PUT',
          body: arrayBuffer,
          headers: {
            'Content-Type': file.type || 'application/octet-stream'
          }
        });
        return uploadedFileKey;
      } catch (error) {
        console.error('S3 upload via presigned URL failed:', error);
        return null;
      }
    },
    []
  );

  const uploadFileFromUri = useCallback(
    (file: File, fileName: string) =>
      uploadFileToS3(file, fileName, 'uploads/admin/images/profile'),
    [uploadFileToS3]
  );

  const uploadOrganisationLogoFromUri = useCallback(
    (file: File, fileName: string) =>
      uploadFileToS3(file, fileName, 'uploads/yourdoc/images/organisation_logo'),
    [uploadFileToS3]
  );

  const generateSignedUrl = useCallback(
    async (s3Key: string): Promise<string> => {
      if (!s3Key) {
        return '';
      }
      const response = await getFetchPresignedUrl(s3Key);
      const signedUrl = response?.generateDownloadPresignedUrl?.downloadUrl || '';
      return signedUrl;
    },
    [] // no need to depend on s3Client anymore
  );

  const sanitizeBase = (s: string) =>
    (s || '')
      .normalize('NFKD') // normalize unicode
      .replace(/[\u0300-\u036f]/g, '') // strip diacritics
      .replace(/[^a-zA-Z0-9]+/g, '-') // non-alnum -> -
      .replace(/^-+|-+$/g, '') // trim -
      .replace(/-{2,}/g, '-') // collapse
      .toLowerCase();

  const sanitizeExt = (s: string) =>
    (s || '')
      .normalize('NFKD')
      .replace(/[^a-zA-Z0-9]/g, '')
      .toLowerCase();

  const getModifiedFileName = useCallback((fileName: string, id: string): string => {
    if (!fileName || !id) return fileName;

    const lastDot = fileName.lastIndexOf('.');
    const hasExt = lastDot > 0 && lastDot < fileName.length - 1;

    const rawBase = hasExt ? fileName.slice(0, lastDot) : fileName;
    const rawExt = hasExt ? fileName.slice(lastDot + 1) : '';

    const base = sanitizeBase(rawBase) || 'upload';
    const ext = sanitizeExt(rawExt);

    // keep id safe too (in case it has weird chars)
    const safeId = sanitizeBase(id) || id;

    return ext ? `${base}_${safeId}.${ext}` : `${base}_${safeId}`;
  }, []);

  return {
    generateSignedUrl,
    getModifiedFileName,
    uploadFileFromUri,
    uploadOrganisationLogoFromUri
  };
};
